import { ApplicationStatus } from '../api/applicationService';
import {
    hasSignatureError,
    holdErrorQueryValue,
    isClosedSignatureStatus,
    isSignatureRetryEligible,
} from './signatureAttempt';

/**
 * Dónde retoma el postulante que vuelve a entrar con un quote cuyo formulario de solicitud
 * ya está empezado.
 *
 * Un solo status de cotización (`in_progress`) cubre cinco estados de solicitud: ole-api-client
 * mapea `in progress`, `complete`, `upload documents`, `in summary` e `in sign` al mismo
 * `quotation.in_progress`. Por eso el link copiado desde el portal no identifica la etapa y hay
 * que resolverla acá, contra el status real de la solicitud.
 *
 * Cada ruta destino fija su propio status al montar (`/confirmation` → `in summary`,
 * `/attachment` → `upload documents`, `/logalty` y `/signature` → `in sign`), así que el mapa
 * elige siempre el destino cuyo status coincide con el entrante: abrir el link no puede
 * retroceder el estado de la solicitud.
 *
 * Puro a propósito: el contexto hace el fetch y ejecuta la navegación; acá solo vive la decisión.
 */

export type ResumeRoute = {
    /** Pathname destino, sin query string. */
    path: string;
    /** Motivo de la decisión, para el log del contexto. */
    reason: string;
    /** Params fijos que el destino necesita para renderizar el estado correcto. */
    params?: Record<string, string>;
    /** El destino identifica al postulante por su email (del summary) y no por el token. */
    identifiesByEmail?: boolean;
};

const APPLICATION: ResumeRoute = { path: '/application', reason: 'form_in_progress' };
const CONFIRMATION: ResumeRoute = { path: '/confirmation', reason: 'form_complete_in_summary' };

/** Pantalla de "solicitud enviada": su copy habla de la confirmación de la evaluación. */
const EVALUATION_MX: ResumeRoute = {
    path: '/congratulations',
    reason: 'under_evaluation',
    identifiesByEmail: true,
};

/**
 * BR no tiene pantalla propia de evaluación: reusa el agradecimiento de la página de firma, que
 * hasta ahora solo se alcanzaba con el string de error de pago del callback de la firma. El param
 * `state` es un discriminante explícito para no depender de ese string — sin él, el fallback de esa
 * página le dice al postulante que rechazó su solicitud.
 */
const EVALUATION_BR: ResumeRoute = {
    path: '/signature-rejected',
    reason: 'under_evaluation_br_thank_you',
    params: { state: 'submitted' },
};

const EDITABLE_STATUSES = [ApplicationStatus.IN_PROGRESS, 'started'];
const SUMMARY_STATUSES = [ApplicationStatus.IN_SUMMARY, 'complete'];
// `paid` entra acá y no en el corte de "sin destino": en BR el flujo termina en pago, así que es el
// estado normal de una solicitud enviada esperando suscripción, y el mapper de ole-api-client lo
// lleva a `quotation.in_evaluation` igual que a los otros dos.
const EVALUATION_STATUSES = ['pending evaluation', 'quality control', 'paid'];

export type ResumeRouteOptions = {
    /** `error` query on the URL the client landed on after Logalty. */
    signatureError?: string | null;
    /** Explicit "Reintentar firma". A page load must not set this. */
    explicitRetry?: boolean;
};

/**
 * Screen that explains the attempt was cancelled and waits for an explicit retry.
 * MX reuses `/logalty` (the page renders the hold state and does not request a SAML).
 * BR already has that screen at `/signature-rejected?error=cancel`.
 */
function signatureHoldRoute(country: string, signatureError?: string | null): ResumeRoute {
    const params = { error: holdErrorQueryValue(signatureError) };
    if (country === 'MX') {
        return {
            path: '/logalty',
            reason: 'signature_cancelled_hold',
            params,
        };
    }
    return {
        path: '/signature-rejected',
        reason: 'signature_cancelled_hold',
        params,
    };
}

function explicitSignatureRoute(country: string): ResumeRoute {
    return country === 'MX'
        ? { path: '/logalty', reason: 'explicit_signature_retry' }
        : { path: '/signature', reason: 'explicit_signature_retry' };
}

/**
 * `signed` devuelve `null` a propósito: su destino no es una ruta de la app sino la sesión de pago,
 * y esa decisión ya vive aguas abajo en el contexto.
 */
export const resolveResumeRoute = (
    appStatus: string | undefined,
    country: string,
    options?: ResumeRouteOptions,
): ResumeRoute | null => {
    if (!appStatus) {
        return null;
    }

    if (EDITABLE_STATUSES.includes(appStatus)) {
        return APPLICATION;
    }

    // INT solo tiene /quote, /application y /confirmation, y su flujo post-formulario nunca se
    // despachó por status. Se lo deja en el comportamiento que ya tenía.
    if (country !== 'MX' && country !== 'BR') {
        return null;
    }

    // The click wins over a closed attempt, and only over that attempt.
    // `retry=1` on a signed, paid, in-evaluation or in-summary application
    // must fall through to the normal destination — a saved link must not
    // open a new ceremony.
    if (options?.explicitRetry && isSignatureRetryEligible(appStatus)) {
        return explicitSignatureRoute(country);
    }

    // `rejected by client` / `cancelled` used to return null, so the summary step
    // (`digital_signature`) fell through and reopened Logalty. Same for `in sign`
    // when the return URL already carries any `error` — MX sends a numeric result
    // code (`99932`), and the webhook may not have changed the status yet.
    // Technical codes are kept on the hold URL so the notice does not say the
    // client cancelled.
    if (
        isClosedSignatureStatus(appStatus) ||
        (appStatus === ApplicationStatus.IN_SIGN && hasSignatureError(options?.signatureError))
    ) {
        return signatureHoldRoute(country, options?.signatureError);
    }

    if (SUMMARY_STATUSES.includes(appStatus)) {
        return CONFIRMATION;
    }

    if (appStatus === ApplicationStatus.UPLOAD_DOCUMENTS) {
        return country === 'MX'
            ? { path: '/attachment', reason: 'upload_documents' }
            : { path: '/signature', reason: 'upload_documents_br_signature' };
    }

    if (appStatus === ApplicationStatus.IN_SIGN) {
        return country === 'MX'
            ? { path: '/logalty', reason: 'in_sign_logalty' }
            : { path: '/signature', reason: 'in_sign_docusign' };
    }

    // La solicitud ya salió a suscripción: no hay etapa del formulario que retomar, así que el
    // destino es la pantalla terminal de "enviada", no el cotizador.
    if (EVALUATION_STATUSES.includes(appStatus)) {
        return country === 'MX' ? EVALUATION_MX : EVALUATION_BR;
    }

    return null;
};

/**
 * Where to send the client when the signature page must not open a ceremony.
 * `signed` has no route in the resume map (payment / congratulations live in
 * the context). MX falls back to the submitted-application screen.
 */
export function destinationWhenSignatureBlocked(
    status: string | null | undefined,
    country: string,
    quoteId: string,
): string | null {
    if (!status) return null;
    if (status.trim().toLowerCase() === ApplicationStatus.SIGNED) {
        return country === 'MX' ? '/congratulations' : null;
    }
    const route = resolveResumeRoute(status, country);
    if (!route) return null;
    const params = new URLSearchParams(route.params ?? {});
    if (!route.identifiesByEmail) {
        params.set('quoteID', quoteId);
    }
    const query = params.toString();
    return query ? `${route.path}?${query}` : route.path;
}
